import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  ArrowPathIcon,
  KeyIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeSlashIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { isWindowsUpdateEntry } from '../utils/software';

const EMPTY_DRAFT = { name: '', version: '', vendor: '', category: 'other', notes: '' };

const LICENSE_BADGE = {
  expired:    { label: 'Caducada',      color: '#ef4444', bg: 'rgba(239,68,68,.12)' },
  expiring:   { label: 'Caduca pronto', color: '#f59e0b', bg: 'rgba(245,158,11,.14)' },
  perpetual:  { label: 'Perpetua',      color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  ok:         { label: 'Vigente',       color: '#22c55e', bg: 'rgba(34,197,94,.12)' },
  none:       { label: 'Sin licencia',  color: '#94a3b8', bg: 'rgba(148,163,184,.12)' },
};

function badgeFor(row) {
  if (!row.license_id) return LICENSE_BADGE.none;
  if (row.license_status === 'cancelled') return { label: 'Cancelada', color: '#94a3b8', bg: 'rgba(148,163,184,.12)' };
  if (row.license_status === 'in_renewal' && row.license_expiry_state !== 'expired') {
    return { label: 'En renovación', color: '#f59e0b', bg: 'rgba(245,158,11,.14)' };
  }
  return LICENSE_BADGE[row.license_expiry_state] || (row.license_status === 'active'
    ? LICENSE_BADGE.ok
    : LICENSE_BADGE.none);
}

function statusBadge(row) {
  if (row.status === 'ignored') return { label: 'Ignorado', color: '#94a3b8' };
  if (row.status === 'missing') return { label: 'Ya no detectado', color: '#f59e0b' };
  if (row.source === 'manual') return { label: 'Manual', color: '#a78bfa' };
  if (row.status === 'confirmed') return { label: 'Confirmado', color: '#22c55e' };
  return { label: 'Detectado', color: '#38bdf8' };
}

export default function SoftwareInventoryPanel({
  companyId,
  hostCiType,
  hostCiId,
  fallbackEntries = [],
  onCountChange,
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], category_labels: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [groupBy, setGroupBy] = useState('none');

  const categoryLabels = meta.category_labels || {};
  const categories = meta.categories || [];
  const canManage = Boolean(hostCiId);

  const load = useCallback(async () => {
    if (!companyId || !hostCiType || !hostCiId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getSoftwareInstallations(companyId, {
        host_ci_type: hostCiType,
        host_ci_id: hostCiId,
        include_ignored: 1,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el software instalado');
      // Si la API aún no tiene filas, al menos se pinta lo que trae el agente.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, hostCiType, hostCiId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!companyId) return;
    api.getSoftwareMeta(companyId).then((m) => setMeta(m || {})).catch(() => {});
  }, [companyId]);
  useEffect(() => {
    if (onCountChange) onCountChange(rows.filter((r) => r.status !== 'ignored').length);
  }, [rows, onCountChange]);

  // Mientras la tabla está vacía, se enseña lo que el agente trajo en el blob.
  const displayRows = useMemo(() => {
    const visibles = (lista) => lista.filter((row) => !isWindowsUpdateEntry(row));
    if (rows.length) return visibles(rows);
    return visibles(fallbackEntries || []).map((entry, index) => ({
      id: null,
      name: entry.name,
      version: entry.version || null,
      vendor: entry.publisher || entry.vendor || null,
      category: 'other',
      source: 'agent',
      status: 'detected',
      license_id: null,
      _fallback: true,
      _key: `fallback-${index}`,
    }));
  }, [rows, fallbackEntries]);

  const visibleCount = displayRows.filter((r) => r.status !== 'ignored').length;

  // Buscador y agrupación: con 120+ entradas en un equipo normal, la tabla
  // plana obliga a bajar con el scroll para encontrar cualquier cosa.
  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return displayRows;
    return displayRows.filter((row) =>
      [row.name, row.version, row.vendor]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [displayRows, query]);

  const listItems = useMemo(() => {
    if (groupBy === 'none') return filteredRows.map((row) => ({ row }));
    const campo = groupBy === 'vendor' ? 'vendor' : 'category';
    const mapa = new Map();
    filteredRows.forEach((row) => {
      const crudo = String(row[campo] || '').trim();
      const clave = groupBy === 'category'
        ? (categoryLabels[crudo] || crudo || 'Sin categoría')
        : (crudo || 'Sin fabricante');
      if (!mapa.has(clave)) mapa.set(clave, []);
      mapa.get(clave).push(row);
    });
    const items = [];
    [...mapa.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'es'))
      .forEach(([clave, filas]) => {
        items.push({ header: clave, count: filas.length, key: `h-${clave}` });
        filas.forEach((row) => items.push({ row }));
      });
    return items;
  }, [filteredRows, groupBy, categoryLabels]);

  const syncHost = async () => {
    try {
      setLoading(true);
      await api.syncHostSoftware(hostCiType, hostCiId, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el inventario');
    } finally {
      setLoading(false);
    }
  };

  const saveNew = async () => {
    if (!String(draft.name || '').trim()) { setError('Indica el nombre del software'); return; }
    setSaving(true);
    setError('');
    try {
      await api.createSoftwareInstallation({
        host_ci_type: hostCiType,
        host_ci_id: hostCiId,
        name: draft.name.trim(),
        version: draft.version.trim(),
        vendor: draft.vendor.trim(),
        category: draft.category,
        notes: draft.notes.trim(),
      }, companyId);
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo añadir el software');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      name: row.name || '',
      version: row.version || '',
      vendor: row.vendor || '',
      category: row.category || 'other',
      notes: row.notes || '',
    });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setError('');
    try {
      await api.updateSoftwareInstallation(editingId, {
        name: editDraft.name,
        version: editDraft.version,
        vendor: editDraft.vendor,
        category: editDraft.category,
        notes: editDraft.notes,
      }, companyId);
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo guardar el software');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (row, status) => {
    try {
      await api.updateSoftwareInstallation(row.id, { status }, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar el software');
    }
  };

  const removeRow = async (row) => {
    if (!window.confirm(`¿Eliminar «${row.name}» del inventario?`)) return;
    try {
      await api.deleteSoftwareInstallation(row.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el software');
    }
  };

  const openLicense = async (row) => {
    if (!hostCiId) {
      setError('Guarda el equipo primero para poder asociarle una licencia');
      return;
    }
    if (row.license_id) {
      navigate(`/apps/licenses?destacar=${row.license_id}&abrir=1`);
      return;
    }
    let installationId = row.id;
    if (!installationId && row._fallback) {
      // Fila que aún no estaba en la tabla: se registra antes de licenciarla.
      try {
        const res = await api.createSoftwareInstallation({
          host_ci_type: hostCiType,
          host_ci_id: hostCiId,
          name: row.name,
          version: row.version || '',
          vendor: row.vendor || '',
          category: row.category || 'other',
        }, companyId);
        installationId = res?.data?.id || res?.id;
      } catch (err) {
        setError(err.message || 'No se pudo registrar el software antes de licenciarlo');
        return;
      }
    }
    if (!installationId) {
      setError('Vuelve a cargar el panel e inténtalo de nuevo');
      return;
    }
    const params = new URLSearchParams({
      nueva: '1',
      product: row.name || '',
      version: row.version || '',
      vendor: row.vendor || '',
      software_installation_id: String(installationId),
      assigned_ci_type: hostCiType || '',
      assigned_ci_id: String(hostCiId || ''),
    });
    navigate(`/apps/licenses?${params.toString()}`);
  };

  return (
    <div className="dv-section">
      <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        Software instalado
        <span style={{ fontSize: '0.72rem', fontWeight: 600, background: 'rgba(16,185,129,.12)', color: '#6ee7b7', borderRadius: 4, padding: '1px 7px' }}>
          {visibleCount}
        </span>
        <span style={{ flex: 1 }} />
        <input
          type="search"
          className="form-control"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nombre, versión o fabricante…"
          style={{ width: 260, padding: '3px 8px', fontSize: '0.8rem' }}
          aria-label="Buscar software"
        />
        <select
          className="form-select"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          style={{ width: 175, padding: '3px 8px', fontSize: '0.8rem' }}
          aria-label="Agrupar software"
          title="Agrupar el listado"
        >
          <option value="none">Sin agrupar</option>
          <option value="vendor">Por fabricante</option>
          <option value="category">Por categoría</option>
        </select>
        {!!hostCiId && (
          <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '2px 8px', fontSize: '0.76rem' }}
            onClick={syncHost} disabled={loading}>
            <ArrowPathIcon style={{ width: 13, height: 13 }} /> Actualizar inventario
          </button>
        )}
        <button type="button" className="dv-btn dv-btn-primary" style={{ padding: '2px 8px', fontSize: '0.76rem' }}
          onClick={() => { setAdding((v) => !v); setError(''); }} disabled={!canManage}
          title={canManage ? 'Añadir software manualmente' : 'Guarda primero el equipo'}>
          <PlusIcon style={{ width: 13, height: 13 }} /> Añadir software
        </button>
      </div>

      {error && <div style={{ marginTop: 8, color: '#f87171', fontSize: '0.82rem' }}>⚠ {error}</div>}
      {!canManage && (
        <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          Guarda el equipo primero para poder añadir software o asociarle una licencia.
        </div>
      )}

      {adding && canManage && (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg-toolbar)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.4fr 1fr', gap: 8 }}>
            <label className="form-label" style={{ margin: 0 }}>Nombre *<input className="form-control" value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Veeam Backup & Replication" /></label>
            <label className="form-label" style={{ margin: 0 }}>Versión<input className="form-control" value={draft.version}
              onChange={(e) => setDraft({ ...draft, version: e.target.value })} placeholder="12.0" /></label>
            <label className="form-label" style={{ margin: 0 }}>Fabricante<input className="form-control" value={draft.vendor}
              onChange={(e) => setDraft({ ...draft, vendor: e.target.value })} placeholder="Veeam Software" /></label>
            <label className="form-label" style={{ margin: 0 }}>Categoría<select className="form-select" value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
              {categories.map((c) => <option key={c} value={c}>{categoryLabels[c] || c}</option>)}
            </select></label>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="dv-btn dv-btn-secondary" onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}>
              <XMarkIcon style={{ width: 14, height: 14 }} /> Cancelar
            </button>
            <button type="button" className="dv-btn dv-btn-primary" onClick={saveNew} disabled={saving}>
              <CheckIcon style={{ width: 14, height: 14 }} /> {saving ? 'Guardando…' : 'Guardar software'}
            </button>
          </div>
        </div>
      )}

      {loading && !displayRows.length ? (
        <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>Cargando software…</div>
      ) : !displayRows.length ? (
        <div style={{ marginTop: 8, color: 'var(--text-muted)', fontSize: '0.82rem' }}>
          No se ha detectado software instalado. Puedes añadirlo manualmente con «Añadir software».
        </div>
      ) : (
        <div style={{ maxHeight: 340, overflowY: 'auto', marginTop: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: 'var(--bg-toolbar)', zIndex: 1 }}>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Nombre</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>Versión</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Fabricante</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Origen</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Licencia</th>
                <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {listItems.map((item) => {
                if (item.header) {
                  return (
                    <tr key={item.key} style={{ background: 'var(--bg-toolbar)' }}>
                      <td
                        colSpan={6}
                        style={{
                          padding: '5px 8px',
                          fontWeight: 700,
                          fontSize: '0.78rem',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {item.header}{' '}
                        <span style={{ fontWeight: 500, color: 'var(--text-muted)' }}>
                          ({item.count})
                        </span>
                      </td>
                    </tr>
                  );
                }
                const row = item.row;
                const badge = badgeFor(row);
                const st = statusBadge(row);
                const isEditing = editingId && row.id === editingId;
                return (
                  <tr key={row.id || row._key} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '4px 8px', color: 'var(--text-primary)' }}>
                      {isEditing ? (
                        <input className="form-control" style={{ padding: '2px 6px', fontSize: '0.8rem' }}
                          value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} />
                      ) : row.name}
                    </td>
                    <td style={{ padding: '4px 8px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <input className="form-control" style={{ padding: '2px 6px', fontSize: '0.8rem', width: 90 }}
                          value={editDraft.version} onChange={(e) => setEditDraft({ ...editDraft, version: e.target.value })} />
                      ) : (row.version || '—')}
                    </td>
                    <td style={{ padding: '4px 8px', color: 'var(--text-muted)' }}>
                      {isEditing ? (
                        <input className="form-control" style={{ padding: '2px 6px', fontSize: '0.8rem' }}
                          value={editDraft.vendor} onChange={(e) => setEditDraft({ ...editDraft, vendor: e.target.value })} />
                      ) : (row.vendor || '—')}
                    </td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: st.color, background: `${st.color}18`, borderRadius: 4, padding: '1px 6px' }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, color: badge.color, background: badge.bg, borderRadius: 4, padding: '1px 6px' }}>
                        {badge.label}
                      </span>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button type="button" className="dv-btn dv-btn-primary" style={{ padding: '1px 6px', fontSize: '0.72rem', marginRight: 4 }}
                            onClick={saveEdit} disabled={saving}><CheckIcon style={{ width: 12, height: 12 }} /> Guardar</button>
                          <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem' }}
                            onClick={() => setEditingId(null)}><XMarkIcon style={{ width: 12, height: 12 }} /> Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem', marginRight: 4 }}
                            onClick={() => openLicense(row)} title={row.license_id ? 'Ver licencia' : 'Asociar licencia'}>
                            <KeyIcon style={{ width: 12, height: 12 }} /> {row.license_id ? 'Licencia' : 'Asociar licencia'}
                          </button>
                          {row.source === 'manual' && row.id != null && (
                            <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem', marginRight: 4 }}
                              onClick={() => startEdit(row)} title="Editar software manual">
                              <PencilSquareIcon style={{ width: 12, height: 12 }} />
                            </button>
                          )}
                          {row.source === 'manual' && row.id != null && (
                            <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem', marginRight: 4 }}
                              onClick={() => removeRow(row)} title="Eliminar software manual">
                              <TrashIcon style={{ width: 12, height: 12 }} />
                            </button>
                          )}
                          {row.source !== 'manual' && row.id != null && row.status !== 'ignored' && (
                            <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem' }}
                              onClick={() => setStatus(row, 'ignored')} title="Ocultar del inventario (se puede restaurar)">
                              <EyeSlashIcon style={{ width: 12, height: 12 }} />
                            </button>
                          )}
                          {row.status === 'ignored' && row.id != null && (
                            <button type="button" className="dv-btn dv-btn-secondary" style={{ padding: '1px 6px', fontSize: '0.72rem' }}
                              onClick={() => setStatus(row, 'detected')} title="Volver a mostrar">
                              <EyeIconInline />
                            </button>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && fallbackEntries.length > 0 && (
            <div style={{ marginTop: 6, color: 'var(--text-muted)', fontSize: '0.76rem' }}>
              Mostrando el inventario del último push del agente. Pulsa «Actualizar inventario» para registrarlo y poder asociar licencias.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EyeIconInline() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}
